// @ts-strict-ignore
export interface LoadedConfig {
	appConfig: AppConfig;
}

export enum Brand {
	Bread = "bread",
	Rbc = "rbc",
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const localEnvironments = ["local", "slice"] as const;
export type LocalEnvironment = (typeof localEnvironments)[number];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const developmentEnvironments = ["dev", "npp-dev-ads"] as const;
export type DevelopmentEnvironment = (typeof developmentEnvironments)[number];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const qaEnvironments = ["qa-rome", "npp-qa-ads"] as const;
export type QAEnvironment = (typeof qaEnvironments)[number];

const previewEnvironments = ["pv-rome", "sp-pv-ads"] as const;
export type PreviewEnvironment = (typeof previewEnvironments)[number];

const productionEnvironments = ["prod-rome", "pp-prod-ads"] as const;
export type ProductionEnvironment = (typeof productionEnvironments)[number];

export type Environment =
	| LocalEnvironment
	| DevelopmentEnvironment
	| QAEnvironment
	| PreviewEnvironment
	| ProductionEnvironment
	// Rakesh Sukla (slack: @Rakesh) said this is rarely used but to keep it
	| "loadtest"
	| "sandbox";

export interface AppConfig {
	/** Backend URL */
	apiurl: string;
	/** Backend URL */
	checkouturl: string;
	/** Bread Redirect Link */
	helpurl?: string;
	/** Bread Redirect Link */
	signageurl?: string;
	/** Backend URL */
	produrl?: string;
	/** Tenant */
	brand: Brand;
	/**
	 * Vendor ID — Datadog RUM application identifier. Embedded in every official browser SDK
	 * init snippet, including the CDN-sync HTML example.
	 * @see https://docs.datadoghq.com/real_user_monitoring/browser/setup/
	 */
	datadogrumapplicationid: string;
	/**
	 * Vendor Token — Datadog RUM client token. Client tokens exist specifically so ingestion
	 * credentials can be embedded in browser code; API keys must never be used client-side.
	 * @see https://docs.datadoghq.com/account_management/api-app-keys/#client-tokens
	 */
	datadogrumclienttoken: string;
	/** Tunable — milliseconds */
	defaultcalltimeout: number;
	/** Environment */
	env: Environment;
	/**
	 * Vendor ID — Okta public client identifier. SPAs are public OAuth clients whose source is
	 * visible to end users, so the client ID is non-secret by design.
	 * @see https://developer.okta.com/docs/concepts/oauth-openid/#is-your-client-public
	 */
	oktaclientid: string;
	/** Vendor URL */
	oktaissuer: string;
	/** Dev Flag */
	usefakeauth: boolean;
	/** Vendor URL */
	adobetagmanagerurl: string;
	/** Business Rule */
	programgroupswithoutmaxcartsize: string[];
	/** Dev Flag */
	mswenabled: boolean;
}

export interface BuildConfig {
	name: string;
	version: string;
}

interface GetConfigParams<T> {
	safeReturn?: boolean;
	defaultValue?: T;
}

export function getConfig<T extends string | number | boolean | string[] = string>(
	configKey: keyof AppConfig | keyof BuildConfig,
	params?: GetConfigParams<T>,
): T {
	const { safeReturn = false, defaultValue } = params || {};
	if (window.AppConfig && window.BuildConfig) {
		const Config = { ...window.AppConfig, ...window.BuildConfig };
		if (configKey in Config) {
			return Config[configKey] as T;
		}
	}

	if (safeReturn) {
		return defaultValue;
	}

	throw new Error(`Unknown config: ${configKey}`);
}

export const isPreviewEnvironment = (): boolean => {
	const env = getConfig<Environment>("env");
	return previewEnvironments.indexOf(env as PreviewEnvironment) !== -1;
};

export const isProductionEnvironment = (): boolean => {
	const env = getConfig<Environment>("env");
	return productionEnvironments.indexOf(env as ProductionEnvironment) !== -1;
};

export const tenants = {
	ROME: "rome",
	ADS: "bread",
};
